// Full Leaderboard / Engagement page — daily/weekly game-wins ranking plus
// (when the engagement-summary's page_enabled is true) the streak & rewards
// path-to-rewards strip, a Top Streaks tab, a streak row in the rank rail,
// and the bottom prize cards section.
// Design source: claude.ai design project "Circlechell leaderboard dashboard"
// (CircleChess Leaderboard.dc.html + react-native reference), with the
// design's "Points" column replaced by Rating per product decision, and the
// engagement additions from scratchpad/engagement_canvas.dc.html.
//
// Layout: when engagement is disabled (page_enabled=false or the summary
// fetch failed), the page renders exactly as it did pre-engagement — the
// breadcrumb, title, tabs and (wide) your-rank rail stay fixed, only the
// ranked rows scroll inside the list card. When engagement is enabled, the
// whole page scrolls (strip → tabs → board+rail → rewards), matching the
// design's single continuous page.
// (The screen is registered in isChessAnalysisScreen so the navigator wraps it
// in a View, not a ScrollView — otherwise the whole page would scroll.)

import React, {useEffect, useRef, useState} from 'react';
import {
  LayoutChangeEvent,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import CCText from '@/Atoms/CCText';
import {ScreenName} from '@/navigators/StackConstants';
import {logEvent} from '@/Utils/GAEvents';
import {
  WIDE_BREAKPOINT,
  lbColors,
  lbRadius,
  lbShadow,
} from './leaderboardTheme';
import {Period, periodMeta} from './types';
import {useLeaderboardData} from './useLeaderboardData';
import {useEngagementData} from './useEngagementData';
import {dailyStreakActionCopy} from './engagementPresentation';
import LeaderboardRow from './components/LeaderboardRow';
import LiveBadge from './components/LiveBadge';
import PeriodTabs from './components/PeriodTabs';
import {SkeletonRows} from './components/Skeleton';
import StickyYourRankBar from './components/StickyYourRankBar';
import YourRankPanel from './components/YourRankPanel';
import PathToRewardsStrip from './components/PathToRewardsStrip';
import PrizeCards from './components/PrizeCards';
import WinnersSection from './components/WinnersSection';

const TOP_N = 20;

// Last-known engagement page_enabled value, used ONLY to pick the initial
// shell (legacy vs engagement) while the summary query is loading — avoids
// the visible reflow of mounting the legacy shell first and remounting into
// the engagement shell once the fetch resolves. Absent/first-time → false,
// i.e. exactly today's behavior; the server value always wins once loaded.
const ENGAGEMENT_SHELL_CACHE_KEY = 'engagement_page_enabled_cache';

function readCachedEngagementEnabled(): boolean {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage.getItem(ENGAGEMENT_SHELL_CACHE_KEY) === 'true';
    }
  } catch (error) {
    console.warn(
      'readCachedEngagementEnabled: localStorage unavailable',
      error,
    );
  }
  return false;
}

function Leaderboard() {
  const navigation = useNavigation();
  const {width} = useWindowDimensions();
  const wide = width >= WIDE_BREAKPOINT;
  const [period, setPeriod] = useState<Period>('daily');
  const meta = periodMeta(period);
  const {rows, me, totalPlayers, leaderWins, isLoading, isError, isSwitching} =
    useLeaderboardData(period, {
      refetchIntervalMs: 25000,
      criteria: meta.criteria,
    });
  // Show skeletons on first load AND while a newly-selected period loads (so we
  // never show the previous period's rows under the new tab).
  const loading = isLoading || isSwitching;

  const {
    summary: engagement,
    presentation: engPresentation,
    isLoading: engagementLoading,
  } = useEngagementData();
  // While the summary is loading, use the cached last-known value as the
  // shell choice; once it resolves, the fresh server value wins and the
  // cache is refreshed for the next visit.
  const [cachedEngagementEnabled] = useState(readCachedEngagementEnabled);
  const engagementEnabled = engagementLoading
    ? cachedEngagementEnabled
    : engagement.pageEnabled;

  useEffect(() => {
    if (engagementLoading) {
      return;
    }
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(
          ENGAGEMENT_SHELL_CACHE_KEY,
          engagement.pageEnabled ? 'true' : 'false',
        );
      }
    } catch (error) {
      console.warn('engagement shell cache: failed to persist', error);
    }
  }, [engagementLoading, engagement.pageEnabled]);

  const pageScrollRef = useRef<ScrollView>(null);
  const rewardsYRef = useRef(0);
  const onRewardsLayout = (e: LayoutChangeEvent) => {
    rewardsYRef.current = e.nativeEvent.layout.y;
  };

  // Engagement + wide: the rail's two stacked cards define the block height;
  // the list card is pinned to it so both columns end on the same line (the
  // list's 20 rows would otherwise out-grow the rail and drive the height).
  const [railHeight, setRailHeight] = useState(0);
  const onRailLayout = (e: LayoutChangeEvent) => {
    const h = Math.round(e.nativeEvent.layout.height);
    setRailHeight(prev => (Math.abs(prev - h) > 1 ? h : prev));
  };
  const scrollToRewards = () => {
    logEvent('event', 'engagement_see_rewards', period);
    pageScrollRef.current?.scrollTo({
      y: Math.max(0, rewardsYRef.current - 16),
      animated: true,
    });
  };

  const top = rows.slice(0, TOP_N);
  const meInTop = me?.rank != null && me.rank <= TOP_N;
  const showStickyBar = !wide && !loading && me != null && !meInTop;

  const onChangePeriod = (p: Period) => {
    setPeriod(p);
    logEvent('event', 'leaderboard_period_change', p);
  };

  const onPlay = () => {
    logEvent('event', 'leaderboard_play_cta', 'full_screen');
    navigation.navigate(ScreenName.PlayChess);
  };

  const streakLine = engPresentation
    ? engagement.milestones.find(m => m.id === engagement.nextMilestone?.id)
        ?.achievedThisPeriod
      ? "You're in this week's draw"
      : engagement.nextMilestone
      ? `${engagement.nextMilestone.daysLeft} more days → ${
          engagement.nextMilestone.label ?? engagement.nextMilestone.prizeShort
        }`
      : ''
    : '';

  const dpSubline = dailyStreakActionCopy(engagement);

  const header = (
    <>
      {/* breadcrumb (static) */}
      <Pressable
        onPress={() => navigation.navigate(ScreenName.Dashboard)}
        style={styles.back}>
        <CCText fontSize={13.5} fontWeight="bold" color={lbColors.muted}>
          ← Dashboard
        </CCText>
        <CCText fontSize={13.5} fontWeight="bold" color={lbColors.ink}>
          {' / Leaderboard'}
        </CCText>
      </Pressable>

      {/* title (static) */}
      <View style={styles.titleRow}>
        <View style={styles.titleIcon}>
          <CCText fontSize={30}>🏆</CCText>
        </View>
        <View style={{flex: 1}}>
          <View style={styles.titleInline}>
            <CCText fontSize={32} fontWeight="bold" color={lbColors.ink}>
              Leaderboard
            </CCText>
            <LiveBadge />
          </View>
          <CCText
            fontSize={14}
            fontWeight="semibold"
            color={lbColors.muted}
            style={{marginTop: 2}}>
            {meta.rankedBySubtitle} · reshuffles as games finish
          </CCText>
        </View>
        {wide ? <PlayCTA onPress={onPlay} /> : null}
      </View>

      {engagementEnabled ? (
        <PathToRewardsStrip
          streakCount={engagement.streak.streakCount}
          todayComplete={engagement.streak.todayComplete}
          subline={dpSubline}
          presentation={engPresentation}
          onSeeRewards={scrollToRewards}
          wide={wide}
        />
      ) : null}

      {/* period tabs (static); Play CTA rides alongside on narrow */}
      <View style={styles.tabsRow}>
        <PeriodTabs period={period} onChange={onChangePeriod} />
        {!wide ? <PlayCTA onPress={onPlay} /> : null}
      </View>
    </>
  );

  const columns = isError ? (
    <View style={[styles.listCard, lbShadow.card, styles.emptyCard]}>
      <CCText fontSize={15} fontWeight="semibold" color={lbColors.muted}>
        Couldn't load the leaderboard. Please try again in a moment.
      </CCText>
    </View>
  ) : (
    <View
      style={[
        engagementEnabled ? styles.columnsEng : styles.columns,
        wide && styles.columnsWide,
      ]}>
      {/* ranked list */}
      <View style={styles.listColumn}>
        <View
          style={[
            styles.listCard,
            // Engagement: HARD-cap the card at the measured rail height (wide)
            // so it ends exactly at the rail's bottom card — no flex fill, no
            // growing past it; rows scroll inside. Narrow (no rail) gets a
            // fixed bound. Legacy keeps the flex-fill viewport behavior.
            engagementEnabled
              ? wide
                ? {
                    height: railHeight > 0 ? railHeight : 620,
                    maxHeight: railHeight > 0 ? railHeight : 620,
                    overflow: 'hidden' as const,
                  }
                : styles.listCardNarrowEng
              : styles.listCardFill,
            lbShadow.card,
          ]}>
          {/* column header (static) */}
          <View style={styles.colHead}>
            <CCText
              fontSize={11}
              fontWeight="bold"
              color={lbColors.faint}
              style={[styles.colHeadText, styles.colRank]}>
              #
            </CCText>
            <View style={{width: 40}} />
            <CCText
              fontSize={11}
              fontWeight="bold"
              color={lbColors.faint}
              style={[styles.colHeadText, {flex: 1}]}>
              PLAYER
            </CCText>
            {meta.showTrend ? (
              <CCText
                fontSize={11}
                fontWeight="bold"
                color={lbColors.faint}
                style={[styles.colHeadText, styles.colTrend]}>
                TREND
              </CCText>
            ) : (
              <View style={styles.colTrend} />
            )}
            {wide ? (
              <CCText
                fontSize={11}
                fontWeight="bold"
                color={lbColors.faint}
                style={[styles.colHeadText, styles.colRating]}>
                RATING
              </CCText>
            ) : null}
            <CCText
              fontSize={11}
              fontWeight="bold"
              color={lbColors.faint}
              style={[styles.colHeadText, styles.colWins]}>
              {meta.metricLabel}
            </CCText>
          </View>

          {loading ? (
            <View style={styles.rowsScroll}>
              <SkeletonRows count={wide ? 9 : 8} wide={wide} />
            </View>
          ) : top.length === 0 ? (
            <View style={styles.emptyCard}>
              <CCText
                fontSize={15}
                fontWeight="semibold"
                color={lbColors.muted}>
                {meta.emptyText}
              </CCText>
            </View>
          ) : (
            <ScrollView
              style={styles.rowsScroll}
              contentContainerStyle={{
                paddingBottom: showStickyBar ? 100 : 6,
              }}>
              {top.map(entry => (
                <LeaderboardRow
                  key={entry.userid}
                  entry={entry}
                  wide={wide}
                  showTrend={meta.showTrend}
                />
              ))}
            </ScrollView>
          )}
        </View>
      </View>

      {/* your-rank rail (wide only, static; hidden while (re)loading).
          In engagement mode the rail's natural height DRIVES the row height —
          the list card stretches to end exactly at the rail's bottom card. */}
      {wide && me && !loading ? (
        <View
          style={engagementEnabled ? styles.railWrapEng : styles.railWrap}
          onLayout={engagementEnabled ? onRailLayout : undefined}>
          <YourRankPanel
            me={me}
            meta={meta}
            totalPlayers={totalPlayers}
            leaderWins={leaderWins}
            streakCount={
              engagementEnabled ? engagement.streak.streakCount : undefined
            }
            streakLine={streakLine}
            onPressStreak={scrollToRewards}
          />
        </View>
      ) : null}
    </View>
  );

  // Rewards + Winners live below the bounded leaderboard card in normal flow —
  // same fixed-page topology as the legacy layout, so the list card keeps its
  // fixed height + inner row scroll and nothing can overlap.
  const rewards = engagementEnabled ? (
    <View onLayout={onRewardsLayout} style={{flexShrink: 0}}>
      <PrizeCards
        milestones={engagement.milestones}
        leaderboardPrize={engagement.leaderboardPrize}
      />
      <WinnersSection enabled={engagement.winnersAvailable} />
    </View>
  ) : null;

  if (engagementEnabled) {
    // Page scrolls (rewards live below the fold on short viewports), but the
    // leaderboard/rail block is CONTENT-sized — the rail's two cards set the
    // height and the list card stretches to match, rows scrolling inside.
    return (
      <View
        style={[
          styles.screen,
          Platform.OS === 'web' ? ({height: '100%'} as any) : null,
        ]}>
        <ScrollView
          ref={pageScrollRef}
          style={{flex: 1}}
          contentContainerStyle={styles.pageBodyScroll}>
          {header}
          {columns}
          {rewards}
        </ScrollView>

        {showStickyBar && me ? (
          <StickyYourRankBar me={me} topN={TOP_N} meta={meta} />
        ) : null}
      </View>
    );
  }

  return (
    <View
      style={[
        styles.screen,
        Platform.OS === 'web' ? ({height: '100%'} as any) : null,
      ]}>
      <View style={styles.pageBody}>
        {header}
        {columns}
      </View>

      {/* sticky your-rank bar — narrow screens, outside the top 20 */}
      {showStickyBar && me ? (
        <StickyYourRankBar me={me} topN={TOP_N} meta={meta} />
      ) : null}
    </View>
  );
}

// Nudge players to play more games (more wins ⇒ climb the board).
function PlayCTA({onPress}: {onPress: () => void}) {
  return (
    <Pressable
      onPress={onPress}
      style={({pressed}) => [styles.playCta, pressed && {opacity: 0.85}]}>
      <CCText fontSize={15}>♟️</CCText>
      <CCText fontSize={14} fontWeight="bold" color="#FFFFFF">
        Play a game
      </CCText>
      <CCText fontSize={16} fontWeight="bold" color="#FFFFFF">
        →
      </CCText>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  screen: {flex: 1, backgroundColor: lbColors.bg},
  pageBody: {
    flex: 1,
    minHeight: 0,
    maxWidth: 1180,
    alignSelf: 'center',
    width: '100%',
    padding: 22,
  },
  // ScrollView content container variant: content-sized (no flex) so nothing
  // inside collapses or overlaps; page scrolls when block + rewards overflow.
  pageBodyScroll: {
    maxWidth: 1180,
    alignSelf: 'center',
    width: '100%',
    padding: 22,
  },
  back: {flexDirection: 'row', alignItems: 'center', marginBottom: 16},
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    marginBottom: 20,
  },
  titleIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    backgroundColor: lbColors.orange,
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleInline: {flexDirection: 'row', alignItems: 'center', gap: 12},
  tabsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 22,
  },
  playCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: lbColors.teal,
    paddingVertical: 11,
    paddingHorizontal: 18,
    borderRadius: lbRadius.pill,
  },
  // content area fills the space under the static header
  columns: {gap: 22, flex: 1, minHeight: 0},
  // Engagement mode: the block is content-sized (no flex fill) — inside the
  // page ScrollView the rail's natural height defines the row, the list card
  // stretches to it, and rewards flow underneath without overlap.
  columnsEng: {gap: 22},
  columnsWide: {flexDirection: 'row', alignItems: 'stretch'},
  listColumn: {flex: 1, minHeight: 0},
  listCard: {
    backgroundColor: lbColors.card,
    borderRadius: lbRadius.xl,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  // fill the column height so the inner ScrollView is bounded and scrolls
  listCardFill: {flex: 1, minHeight: 0},
  listCardNarrowEng: {height: 560, maxHeight: 560, overflow: 'hidden'},
  rowsScroll: {flex: 1, minHeight: 0},
  railWrap: {alignSelf: 'flex-start'},
  // Engagement wide mode: rail keeps its NATURAL stacked-cards height
  // (flex-start, no stretch) — we measure it and pin the list card to match,
  // so both columns end on the same line without circular flex sizing.
  railWrapEng: {alignSelf: 'flex-start'},
  colHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingHorizontal: 16,
    paddingBottom: 10,
    paddingTop: 4,
    borderBottomWidth: 1,
    borderBottomColor: lbColors.line,
  },
  colHeadText: {letterSpacing: 0.5},
  colRank: {width: 34, textAlign: 'center'},
  colTrend: {width: 46, textAlign: 'center'},
  colRating: {width: 84, textAlign: 'right'},
  colWins: {width: 60, textAlign: 'right'},
  emptyCard: {padding: 28, alignItems: 'center'},
});

export default Leaderboard;
